"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTracking = void 0;
const react_1 = require("react");
const context_1 = require("../context");
const feature_flags_1 = require("../feature-flags");
const AMPLITUDE_API_KEY = 'dcd5e175336b762e9ccc3b6686a23b75';
const CLARITY_PROJECT_ID = 'v936vieorm';
const getAmplitudeApis = () => {
    const { amplitude, sessionReplay } = window;
    if (!amplitude || !sessionReplay) {
        return null;
    }
    if (typeof amplitude.init !== 'function' || typeof amplitude.add !== 'function' || typeof sessionReplay.plugin !== 'function') {
        return null;
    }
    return { amplitude, sessionReplay };
};
const initializeAmplitude = () => {
    if (document.getElementById('amplitude-sdk')) {
        return;
    }
    if (document.querySelector(`script[src="https://cdn.amplitude.com/script/${AMPLITUDE_API_KEY}.js"]`)) {
        return;
    }
    const script = document.createElement('script');
    script.id = 'amplitude-sdk';
    script.src = `https://cdn.amplitude.com/script/${AMPLITUDE_API_KEY}.js`;
    script.async = true;
    script.onload = () => {
        const apis = getAmplitudeApis();
        if (apis) {
            apis.amplitude.add(apis.sessionReplay.plugin({ sampleRate: 0.1 }));
            apis.amplitude.init(AMPLITUDE_API_KEY, {
                fetchRemoteConfig: true,
                autocapture: {
                    attribution: true,
                    fileDownloads: true,
                    formInteractions: true,
                    pageViews: true,
                    sessions: true,
                    elementInteractions: true,
                    networkTracking: true,
                    webVitals: true,
                    frustrationInteractions: {
                        errorClicks: true,
                        deadClicks: true,
                        rageClicks: true,
                    },
                },
            });
        }
    };
    document.head.appendChild(script);
};
const initializeClarity = () => {
    if (document.getElementById('clarity-sdk')) {
        return;
    }
    if (document.querySelector(`script[src="https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}"]`)) {
        return;
    }
    const clarityWindow = window;
    if (!clarityWindow.clarity) {
        const clarity = (...args) => {
            if (clarity.q) {
                clarity.q.push(args);
            }
            else {
                clarity.q = [args];
            }
        };
        clarityWindow.clarity = clarity;
    }
    const script = document.createElement('script');
    script.id = 'clarity-sdk';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
    const firstScript = document.getElementsByTagName('script').item(0);
    if (firstScript === null || firstScript === void 0 ? void 0 : firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
    }
    else {
        document.head.appendChild(script);
    }
};
const useTracking = () => {
    const context = (0, react_1.useContext)(context_1.EnrichContext);
    const initializedRef = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        if (!context || initializedRef.current) {
            return;
        }
        const { featureFlagsService, logError } = context;
        const amplitudeEnabled = featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.EnableTracking)
            || featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.EnableAmplitudeTracking);
        const clarityEnabled = featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.EnableTracking)
            || featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.EnableClarityTracking);
        if (!amplitudeEnabled && !clarityEnabled) {
            return;
        }
        initializedRef.current = true;
        try {
            if (amplitudeEnabled) {
                initializeAmplitude();
            }
            if (clarityEnabled) {
                initializeClarity();
            }
        }
        catch (error) {
            logError('Failed to initialize tracking', error);
        }
    }, [context]);
};
exports.useTracking = useTracking;
